/**
 * EB-1C Visa Criteria Definitions
 *
 * The EB-1C is an immigrant visa for multinational managers or executives
 * transferring to a U.S. company.
 *
 * Legal Reference: 8 CFR 204.5(j), https://www.uscis.gov/policy-manual/volume-6-part-f-chapter-4
 */

import { VisaCriterion } from './types';

/**
 * EB-1C visa criteria
 *
 * The beneficiary must demonstrate:
 * - One year of employment abroad with the same employer
 * - Entering the U.S. to continue in managerial/executive capacity
 * - Job offer from U.S. employer
 * - Documentary evidence of managerial or executive role
 */
export const EB1C_CRITERIA: VisaCriterion[] = [
  {
    id: 'eb1c_managerial_capacity',
    name: 'Managerial Capacity',
    description: 'Evidence that the beneficiary primarily manages the organization, department, subdivision, function, or component; supervises and controls the work of other supervisory, professional, or managerial employees; possesses authority to hire and fire or recommend personnel actions; and exercises discretion over day-to-day operations',
    weight: 10,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'ORGANIZATIONAL_CHART',
      'JOB_DESCRIPTION',
      'CRITICAL_ROLE_DOCUMENT'
    ],
    legalReference: '8 CFR 204.5(j)(2)',
    minimumEvidence: 3
  },
  {
    id: 'eb1c_executive_capacity',
    name: 'Executive Capacity',
    description: 'Evidence that the beneficiary directs the management of the organization or a major component/function; establishes goals and policies; exercises wide latitude in discretionary decision-making; and receives only general supervision or direction from higher-level executives, the board, or stockholders',
    weight: 10,
    requiredDocuments: [
      'EMPLOYMENT_CONTRACT',
      'ORGANIZATIONAL_CHART',
      'EXECUTIVE_ROLE_DOCUMENT',
      'BOARD_RESOLUTION'
    ],
    legalReference: '8 CFR 204.5(j)(2)',
    minimumEvidence: 3
  },
  {
    id: 'eb1c_function_manager',
    name: 'Function Manager',
    description: 'Evidence that the beneficiary manages an essential function within the organization or manages a department, subdivision, or component; and the function, department, or component is managed at a senior level within the organizational hierarchy or with respect to the function managed',
    weight: 9,
    requiredDocuments: [
      'ORGANIZATIONAL_CHART',
      'FUNCTION_DESCRIPTION',
      'MANAGEMENT_REPORT'
    ],
    legalReference: '8 CFR 204.5(j)(2)',
    minimumEvidence: 2
  },
  {
    id: 'eb1c_qualifying_employment',
    name: 'Qualifying Foreign Employment',
    description: 'Evidence of one year of employment abroad with the same employer, affiliate, or subsidiary in a managerial or executive capacity within the three years preceding admission to the United States',
    weight: 10,
    requiredDocuments: [
      'FOREIGN_EMPLOYMENT_LETTER',
      'SALARY_STATEMENT',
      'TAX_RETURN',
      'CORPORATE_AFFILIATION_DOCUMENTS'
    ],
    legalReference: '8 CFR 204.5(j)(3)(i)',
    minimumEvidence: 3
  }
];

/**
 * Get EB-1C criteria configuration
 */
export const EB1C_CONFIG = {
  visaType: 'EB-1C',
  criteria: EB1C_CRITERIA
};
